import { useMemo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import uniqBy from 'lodash.uniqby';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography } from '@components/Typography';
import Autocomplete from '@components/Autocomplete';
import { Input } from '@components/Input';
import PhoneInput from '@components/PhoneInput';
import { Checkbox } from '@components/Checkbox';
import { MultiSelectLabels } from '@components/MultiSelectLabels';
import { alertActions, contactsActions } from '@actions';
import { contactsService } from '@services';
import { handleApiError } from '@helpers/api';
import { preparePhoneNumberForSubmit, setInitialPhoneNumberToForm } from '@helpers/phone';
import { ModalType } from '@constants/modal';
import { useTranslation, useYupValidationResolver, useLocations } from '@hooks';
import { Modal, Content, ActivityWrapper, CheckboxWrapper, StyledInstagramInput, StyledFacebookInput } from './styles';
import { LabelType } from '@constants/labels';

const EditContactModal = ({ modal, onUpdateCallback }) => {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const account = useSelector(state => state.account?.account);
    const { contact } = useSelector(state => state.contacts);
    const { data: labels } = useSelector(state => state.labels);

    const { mapSelectedLocationsToFormValue, getLocationsOptions } = useLocations();
    const isMobile = useMediaQuery('@media (max-width: 600px)');
    const dispatch = useDispatch();

    const labelsOptions = useMemo(() => labels.map(item => ({ value: item.id, label: item.name })), [labels]);

    const ContactSchema = yup.object().shape(
        {
            email: yup.string().when('email', {
                is: val => !val?.length,
                then: yup.string().notRequired(),
                otherwise: yup.string().required('validation.required').email('validation.wrongEmailFormat'),
            }),
            phoneNumber: yup.string().validatePhone('validation.wrongPhoneFormat', true),
            companies: yup.array().min(1, 'validation.required'),
        },
        [['email', 'email']],
    );

    const resolver = useYupValidationResolver(ContactSchema);

    const {
        getValues,
        setValue,
        control,
        formState: { isValid, isDirty, dirtyFields, touchedFields },
    } = useForm({
        mode: 'all',
        resolver: data => resolver(data, { dirtyFields, touchedFields }),
        defaultValues: {
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: '',
            facebookProfile: '',
            instagramProfile: '',
            companies: [],
            unsubscribed: false,
            labels: [],
        },
    });

    const locationsOptions = useMemo(() => getLocationsOptions(), []);

    useEffect(() => {
        const isEdit = modal.type === ModalType.Edit && contact;

        if (isEdit) {
            setValue('phoneNumber', setInitialPhoneNumberToForm(contact?.phoneNumber, account?.locale), {
                shouldValidate: true,
                shouldTouch: true,
            });
            setValue('firstName', contact?.firstName || '');
            setValue('lastName', contact?.lastName || '');

            setValue('email', contact?.email || '', { shouldValidate: true });
            setValue('facebookProfile', contact?.facebookProfile || '', { shouldValidate: true });
            setValue('instagramProfile', contact?.instagramProfile || '', { shouldValidate: true });
            setValue('companies', mapSelectedLocationsToFormValue(contact?.companies), {
                shouldValidate: true,
            });
            setValue('labels', contact?.labels?.map(l => l.id) || [], { shouldValidate: true });
            setValue('unsubscribed', !!contact?.unsubscribed, { shouldValidate: true });
        } else {
            setValue('phoneNumber', setInitialPhoneNumberToForm('', account?.locale), {
                shouldValidate: false,
                shouldTouch: true,
            });
        }
    }, [contact, modal.type]);

    const onSaveContact = async () => {
        const values = getValues();
        setIsSubmitting(true);
        try {
            const newContact = await contactsService.createOrUpdateContact({
                ...(modal.type === ModalType.Edit && { id: contact?.id, ...contact }),
                ...values,
                companies: values.companies.map(c => c.id),
                labels: labels.filter(label => values.labels.includes(label.id)),
                accountId: account?.id,
                phoneNumber: preparePhoneNumberForSubmit(values?.phoneNumber, true),
            });
            dispatch(contactsActions.setContact(newContact));
            if (onUpdateCallback) {
                // INFO: callback to update contact during edit or merge
                onUpdateCallback(newContact, newContact?.id);
            }
            dispatch(
                alertActions.success(
                    t(`alertMessages.${modal.type === ModalType.Create ? 'createSuccess' : 'editSuccess'}`),
                ),
            );
            onCloseEditModal();
        } catch (error) {
            handleApiError(error, `alertMessages.${modal.type === ModalType.Create ? 'createFail' : 'editFail'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onOpenDeleteModal = () => dispatch(contactsActions.setContactDeleteModal(true));

    const onOpenMergeModal = () => dispatch(contactsActions.setContactMergeModal(true));

    const onCloseEditModal = () =>
        dispatch(contactsActions.setContactEditModal({ open: false, type: ModalType.Create }));

    // INFO: update contact labels
    const onUpdateLabels = updatedLabels => {
        const contactLabels = uniqBy([...updatedLabels, ...(contact?.labels || [])], 'id');
        dispatch(contactsActions.setContact({ ...contact, labels: contactLabels }));
    };

    // INFO: delete contact label
    const onDeleteLabel = labelId => {
        const contactLabels = contact?.labels?.filter(l => l.id !== labelId);
        dispatch(contactsActions.setContact({ ...contact, labels: contactLabels }));
    };

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onCloseEditModal}
            title={modal.type === ModalType.Create ? t('ContactsPage.createTitle') : t('ContactsPage.editTitle')}
            subtitle={
                <Typography variant="caption" color="textSecondary">
                    {modal.type === ModalType.Create
                        ? t('ContactsPage.createSubtitle')
                        : t('ContactsPage.editSubtitle')}
                </Typography>
            }
            onPrimaryAction={onSaveContact}
            primaryActionText={modal.type === ModalType.Create ? t('buttons.create') : t('buttons.saveChanges')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onSecondaryAction={onCloseEditModal}
            secondaryActionText={t('buttons.cancel')}
            onAdditionalAction={modal.type === ModalType.Edit ? onOpenDeleteModal : null}
            additionalTextButton={modal.type === ModalType.Edit ? t('buttons.delete') : null}
            onSecondAdditionalAction={modal.type === ModalType.Edit ? onOpenMergeModal : null}
            secondAdditionalTextButton={modal.type === ModalType.Edit ? t('buttons.merge') : null}
        >
            <Content>
                <Controller
                    control={control}
                    name="firstName"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.firstName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="lastName"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.lastName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? error.message : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState: { error } }) => (
                        <PhoneInput
                            placeholder=""
                            label={t('common.phoneNumber')}
                            inputProps={{ name: 'phoneNumber' }}
                            value={field.value}
                            autoFormat
                            onChange={value => {
                                setValue('phoneNumber', value, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                    shouldTouch: true,
                                });
                            }}
                            error={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.email')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="facebookProfile"
                    render={({ field, fieldState: { error }, ref }) => (
                        <StyledFacebookInput
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('ContactsPage.facebookProfile')}
                            fullWidth
                            startAdornment="facebook.com/"
                            error={!!error}
                            helperText={error ? error.message : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="instagramProfile"
                    render={({ field, fieldState: { error }, ref }) => (
                        <StyledInstagramInput
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('ContactsPage.instagramProfile')}
                            fullWidth
                            startAdornment="instagram.com/"
                            error={!!error}
                            helperText={error ? error.message : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="companies"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Autocomplete
                            {...field}
                            ref={ref}
                            label={t('common.locations')}
                            options={locationsOptions}
                            value={field.value}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            inputProps={{ style: { display: field?.value?.length > 0 ? 'none' : 'block' } }}
                            onChangeAutoComplete={items => {
                                setValue('companies', items.length ? items : [], {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }}
                            groupMultiProps={{
                                getOptionLabel: option => option.label,
                            }}
                            required
                            error={error}
                            className="companies-autocomplete"
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="labels"
                    render={({ field, ref }) => (
                        <div style={{ gridColumn: isMobile ? '1 / span 1' : '1 / span 2' }}>
                            <MultiSelectLabels
                                {...field}
                                ref={ref}
                                onChange={value => {
                                    setValue('labels', value, { shouldDirty: true });
                                }}
                                options={labelsOptions}
                                label={t('common.labels')}
                                fullWidth
                                type={LabelType.Contact}
                                onUpdateCallback={onUpdateLabels}
                                onDeleteCallback={onDeleteLabel}
                                onCreateCallback={newLabel => {
                                    const values = getValues();
                                    setValue('labels', [...values.labels, newLabel], {
                                        shouldDirty: true,
                                    });
                                }}
                            />
                        </div>
                    )}
                />
                {modal.type === ModalType.Edit ? (
                    <>
                        <CheckboxWrapper>
                            <Controller
                                control={control}
                                name="unsubscribed"
                                render={({ field, ref }) => (
                                    <Checkbox
                                        ref={ref}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        onChange={e =>
                                            setValue('unsubscribed', e.target.checked, { shouldDirty: true })
                                        }
                                        checked={field.value}
                                    />
                                )}
                            />
                            <Typography variant="body2">{t('ContactsPage.unsubscribe')}</Typography>
                        </CheckboxWrapper>
                        <ActivityWrapper>
                            {contact?.createdOn ? (
                                <div>
                                    <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                                        {t('ContactsPage.createdOn')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {format(new Date(contact?.createdOn), 'dd MMM yyyy')}
                                    </Typography>
                                </div>
                            ) : null}
                            {contact?.lastActiveOn ? (
                                <div>
                                    <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                                        {t('ContactsPage.lastActiveOn')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {format(new Date(contact?.lastActiveOn), 'dd MMM yyyy, HH:mm')}
                                    </Typography>
                                </div>
                            ) : null}
                        </ActivityWrapper>
                    </>
                ) : null}
            </Content>
        </Modal>
    );
};

export default EditContactModal;
